import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, User, Building, Phone, Users2, Archive, RotateCcw, Trash2, MoreVertical, Pencil, HardHat, Wrench, Truck, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const TYPE_LABEL = { taseron: "Taşeron", tedarikci: "Tedarikçi", musteri: "Müşteri", personel: "Personel" };
const TYPE_ICON = { taseron: HardHat, tedarikci: Truck, musteri: User, personel: Wrench };

const EMPTY_FORM = { name: "", type: "tedarikci", phone: "", email: "", tax_office: "", tax_no: "", address: "" };

export default function Cariler() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [archived, setArchived] = useState([]);
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    const [{ data: active }, { data: arch }] = await Promise.all([
      api.get("/cariler?archived=false"),
      api.get("/cariler?archived=true"),
    ]);
    setItems(active);
    setArchived(arch);
    setSelected(new Set());
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setSelected(new Set()); }, [tab]);

  const filtered = useMemo(() => {
    if (tab === "archive") return archived;
    if (tab === "all") return items;
    return items.filter((c) => c.type === tab);
  }, [items, archived, tab]);

  const allIds = useMemo(() => filtered.map((c) => c.id), [filtered]);
  const allChecked = allIds.length > 0 && selected.size === allIds.length;

  const openCreate = () => {
    setEditingId(null);
    // Pre-select type based on current tab
    const preType = ["taseron", "tedarikci", "musteri", "personel"].includes(tab) ? tab : "tedarikci";
    setForm({ ...EMPTY_FORM, type: preType });
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || "",
      type: c.type || "tedarikci",
      phone: c.phone || "",
      email: c.email || "",
      tax_office: c.tax_office || "",
      tax_no: c.tax_no || "",
      address: c.address || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editingId) {
        await api.patch(`/cariler/${editingId}`, form);
        toast.success("Cari güncellendi");
      } else {
        await api.post("/cariler", form);
        toast.success("Cari eklendi");
      }
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => (prev.size === allIds.length ? new Set() : new Set(allIds)));
  };

  const bulkArchive = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} cariyi arşive taşımak istediğinize emin misiniz?`)) return;
    await api.post("/cariler/bulk-delete", { ids: Array.from(selected) });
    toast.success("Cariler arşivlendi");
    load();
  };

  const bulkRestore = async () => {
    if (selected.size === 0) return;
    await api.post("/cariler/bulk-restore", { ids: Array.from(selected) });
    toast.success("Cariler geri getirildi");
    load();
  };

  const bulkPurge = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} cariyi KALICI olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    await api.post("/cariler/bulk-purge", { ids: Array.from(selected) });
    toast.success("Cariler kalıcı olarak silindi");
    load();
  };

  const archiveCari = async (id) => {
    if (!window.confirm("Bu cariyi arşive taşımak istediğinize emin misiniz?")) return;
    await api.delete(`/cariler/${id}`);
    toast.success("Cari arşive taşındı");
    load();
  };

  const restoreCari = async (id) => {
    await api.post(`/cariler/${id}/restore`);
    toast.success("Cari geri getirildi");
    load();
  };

  const purgeCari = async (id) => {
    if (!window.confirm("Bu cariyi KALICI olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.")) return;
    await api.delete(`/cariler/${id}/purge`);
    toast.success("Cari kalıcı olarak silindi");
    load();
  };

  const cnt = (t) => items.filter((c) => c.type === t).length;
  const isArch = tab === "archive";
  const canBulk = selected.size > 0;

  return (
    <div className="space-y-6" data-testid="cariler-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">İlişkiler</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Cari Hesaplar</h1>
        </div>
        {hasPerm("cariler.create") && (
          <Button data-testid="new-cari-btn" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" /> Yeni Cari
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="cari-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="all" data-testid="tab-all">Tümü ({items.length})</TabsTrigger>
          <TabsTrigger value="musteri" data-testid="tab-musteri"><User className="w-3 h-3 mr-1" /> Müşteriler ({cnt("musteri")})</TabsTrigger>
          <TabsTrigger value="taseron" data-testid="tab-taseron"><HardHat className="w-3 h-3 mr-1" /> Taşeron ({cnt("taseron")})</TabsTrigger>
          <TabsTrigger value="tedarikci" data-testid="tab-tedarikci"><Truck className="w-3 h-3 mr-1" /> Tedarikçi ({cnt("tedarikci")})</TabsTrigger>
          <TabsTrigger value="personel" data-testid="tab-personel"><Wrench className="w-3 h-3 mr-1" /> Personel ({cnt("personel")})</TabsTrigger>
          <TabsTrigger value="archive" data-testid="tab-archive"><Archive className="w-3 h-3 mr-1" /> Arşiv ({archived.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Bulk toolbar + Select all */}
      {filtered.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="cari-select-all" />
            <span className="font-medium">Tümünü seç</span>
            {canBulk && <span className="text-muted-foreground text-xs">({selected.size} seçili)</span>}
          </label>
          {canBulk && (
            <div className="flex items-center gap-2 flex-wrap">
              {!isArch && hasPerm("cariler.delete") && (
                <Button size="sm" variant="destructive" onClick={bulkArchive} data-testid="bulk-archive">
                  <Archive className="w-3 h-3 mr-1" /> Arşivle ({selected.size})
                </Button>
              )}
              {isArch && hasPerm("cariler.edit") && (
                <Button size="sm" variant="outline" onClick={bulkRestore} data-testid="bulk-restore">
                  <RotateCcw className="w-3 h-3 mr-1" /> Geri Getir ({selected.size})
                </Button>
              )}
              {isArch && hasPerm("cariler.delete") && (
                <Button size="sm" variant="destructive" onClick={bulkPurge} data-testid="bulk-purge">
                  <Trash2 className="w-3 h-3 mr-1" /> Kalıcı Sil ({selected.size})
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                <X className="w-3 h-3 mr-1" /> Temizle
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => {
          const Icon = TYPE_ICON[c.type] || User;
          const isSel = selected.has(c.id);
          return (
            <Card
              key={c.id}
              data-testid={`cari-card-${c.id}`}
              className={`p-5 hover:shadow-sm shadow-none rounded-md transition-shadow group relative ${isArch ? "opacity-70" : ""} ${isSel ? "ring-2 ring-brand" : ""}`}
            >
              {/* Row 1: checkbox + type badge + menu */}
              <div className="flex items-start justify-between mb-3 gap-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={isSel}
                    onCheckedChange={() => toggle(c.id)}
                    data-testid={`cari-check-${c.id}`}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="w-10 h-10 rounded-md bg-secondary flex items-center justify-center">
                    <Icon className="w-5 h-5 text-brand" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold bg-secondary text-muted-foreground">
                    {TYPE_LABEL[c.type]}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="p-1 rounded hover:bg-secondary opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`cari-menu-${c.id}`}
                      >
                        <MoreVertical className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      {!isArch && hasPerm("cariler.edit") && (
                        <DropdownMenuItem onClick={() => openEdit(c)} data-testid={`cari-edit-${c.id}`}>
                          <Pencil className="w-4 h-4 mr-2" /> Düzenle
                        </DropdownMenuItem>
                      )}
                      {isArch && hasPerm("cariler.edit") && (
                        <DropdownMenuItem onClick={() => restoreCari(c.id)} data-testid={`cari-restore-${c.id}`}>
                          <RotateCcw className="w-4 h-4 mr-2" /> Geri Getir
                        </DropdownMenuItem>
                      )}
                      {isArch && hasPerm("cariler.delete") && (
                        <DropdownMenuItem onClick={() => purgeCari(c.id)} data-testid={`cari-purge-${c.id}`} className="text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" /> Kalıcı Sil
                        </DropdownMenuItem>
                      )}
                      {!isArch && hasPerm("cariler.delete") && (
                        <DropdownMenuItem onClick={() => archiveCari(c.id)} className="text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" /> Arşive Taşı
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              {/* Row 2: name + info — clickable to open detail (unless archive) */}
              <button
                type="button"
                onClick={isArch ? undefined : () => navigate(`/cari/${c.id}`)}
                className={`w-full text-left ${isArch ? "cursor-default" : "cursor-pointer"}`}
                data-testid={`cari-open-${c.id}`}
              >
                <div className="font-heading font-semibold text-base tracking-tight mb-2">{c.name}</div>
                <div className="space-y-1 text-xs text-muted-foreground">
                  {c.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" />{c.phone}</div>}
                  {c.tax_office && <div className="flex items-center gap-2"><Building className="w-3 h-3" />{c.tax_office} · {c.tax_no}</div>}
                  {!c.phone && !c.tax_office && <div className="text-muted-foreground/60 italic">Bilgi eklenmemiş</div>}
                </div>
              </button>
              {/* Quick edit shortcut */}
              {!isArch && hasPerm("cariler.edit") && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openEdit(c); }}
                  className="absolute bottom-3 right-3 p-1.5 rounded-md bg-secondary/60 hover:bg-brand hover:text-white opacity-0 group-hover:opacity-100 transition-all"
                  data-testid={`cari-quick-edit-${c.id}`}
                  title="Düzenle"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-sm text-muted-foreground p-12 text-center border border-dashed border-border rounded-md">
            {tab === "archive"
              ? "Arşivde cari yok."
              : tab === "musteri"
                ? "Henüz müşteri yok. Sözleşme imzalandığında alıcı otomatik müşteri olarak eklenir."
                : tab === "personel"
                  ? "Henüz personel kaydı yok. Şantiye personeli (usta, mühendis, kalfa vb.) eklemek için 'Yeni Cari' butonunu kullanın."
                  : "Cari kaydı yok."}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(EMPTY_FORM); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Cari Düzenle" : "Yeni Cari"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Ad / Firma Adı</Label>
              <Input data-testid="cari-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Tür</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="cari-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="musteri">Müşteri</SelectItem>
                    <SelectItem value="taseron">Taşeron</SelectItem>
                    <SelectItem value="tedarikci">Tedarikçi</SelectItem>
                    <SelectItem value="personel">Personel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Telefon</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2"><Label>E-posta</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Vergi Dairesi</Label>
                <Input value={form.tax_office} onChange={(e) => setForm({ ...form, tax_office: e.target.value })} />
              </div>
              <div className="space-y-2"><Label>Vergi / TC No</Label>
                <Input value={form.tax_no} onChange={(e) => setForm({ ...form, tax_no: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2"><Label>Adres</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="cari-save" onClick={save} disabled={!form.name}>{editingId ? "Kaydet" : "Ekle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
